const PACKAGE_SCALAR_FIELDS = [
  'sn',
  'packageName',
  'packageType',
  'logo',
  'color',
  'durationType',
  'noOfMonths',
  'noOfWeeks',
  'noOfDays',
  'noOfMembers',
  'assigntoMembers',
  'description',
  'packageFee',
  'registerationFee',
  'packageFeeType',
  'ageGroupFrom',
  'ageGroupTo',
  'packageRegFeeType',
  'isGreaterthanNoOfMembers',
  'isDelete',
  'createdBy',
  'modifiedBy',
  'groupFeePayByOnePerson',
  'sameHistory',
  'freezingDays',
  'isTimeBased',
] as const;

const CONTACT_FIELDS = [
  'name',
  'companyName',
  'email',
  'contactNo',
  'officeContactNo',
  'officeAddress',
  'companyCard',
] as const;

const ZONE_FIELDS = [
  'id',
  'zone_code',
  'clientID',
  'ZoneName',
  'zoneID',
  'description',
  'iuser1',
  'cuser1',
  'IsSelect',
  'defaultZone',
  'company',
  'branch',
] as const;

/** 24-char hex id for package builder upsert (backend requires _id). */
export function generatePackageBuilderObjectId(): string {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`.slice(0, 24);
}

function buildCompanyContactPerson(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const c = raw as Record<string, unknown>;
  const contact: Record<string, unknown> = {};
  for (const key of CONTACT_FIELDS) {
    const value = c[key];
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'number' || typeof value === 'boolean') {
      contact[key] = value;
    } else {
      contact[key] = String(value);
    }
  }
  return Object.keys(contact).length ? contact : undefined;
}

function buildZoneItem(raw: Record<string, unknown>): Record<string, unknown> {
  const zone: Record<string, unknown> = {};
  for (const key of ZONE_FIELDS) {
    const value = raw[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'number') {
      zone[key] = value;
    } else {
      zone[key] = String(value);
    }
  }
  return zone;
}

function buildExceptionItem(raw: Record<string, unknown>): Record<string, unknown> {
  const exception: Record<string, unknown> = {
    startTime: String(raw.startTime ?? ''),
    endTime: String(raw.endTime ?? ''),
  };
  const label = raw.label != null ? String(raw.label).trim() : '';
  if (label) {
    exception.label = label;
  }
  if (Array.isArray(raw.daysOfWeek) && raw.daysOfWeek.length) {
    exception.daysOfWeek = raw.daysOfWeek
      .map((d) => Number(d))
      .filter((d) => Number.isFinite(d));
  }
  if (Array.isArray(raw.zones)) {
    exception.zones = raw.zones.map((z) =>
      buildZoneItem(z as Record<string, unknown>),
    );
  } else {
    exception.zones = [];
  }
  return exception;
}

function appendExceptions(
  payload: Record<string, unknown>,
  p: Record<string, unknown>,
): void {
  if (p.isTimeBased === false) {
    payload.exceptions = [];
    return;
  }
  if (!Array.isArray(p.exceptions)) {
    return;
  }
  payload.exceptions = p.exceptions.map((ex) =>
    buildExceptionItem(ex as Record<string, unknown>),
  );
}

export interface PackageBuilderCreateOptions {
  company: string;
  branch: string;
  _id?: string;
}

/** Body for POST .../branches/:branchId/package-builders (CreatePackageBuilderDto). */
export function buildPackageBuilderCreatePayload(
  pkg: object,
  options: PackageBuilderCreateOptions,
): Record<string, unknown> {
  const p = (pkg || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    _id: options._id ?? p._id ?? generatePackageBuilderObjectId(),
    company: options.company,
    branch: options.branch,
  };

  for (const key of PACKAGE_SCALAR_FIELDS) {
    const value = p[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'number' || typeof value === 'boolean') {
      payload[key] = value;
    } else {
      payload[key] = String(value);
    }
  }

  const contact = buildCompanyContactPerson(p.companyContactPerson);
  if (contact) payload.companyContactPerson = contact;

  if (Array.isArray(p.zones)) {
    payload.zones = p.zones.map((z) => buildZoneItem(z as Record<string, unknown>));
  }

  appendExceptions(payload, p);

  return payload;
}

export interface PackageBuilderUpdateOptions {
  _id: string;
  modifiedBy?: string;
}

/** Body for PUT .../branches/:branchId/package-builders/:id (UpdatePackageBuilderDto). */
export function buildPackageBuilderUpdatePayload(
  pkg: object,
  options: PackageBuilderUpdateOptions,
): Record<string, unknown> {
  const p = (pkg || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    _id: options._id,
  };

  if (options.modifiedBy) {
    payload.modifiedBy = options.modifiedBy;
  }

  for (const key of PACKAGE_SCALAR_FIELDS) {
    if (key === 'modifiedBy' && options.modifiedBy) continue;
    const value = p[key];
    if (value === undefined || value === null) continue;
    if (typeof value === 'number' || typeof value === 'boolean') {
      payload[key] = value;
    } else {
      payload[key] = String(value);
    }
  }

  const contact = buildCompanyContactPerson(p.companyContactPerson);
  if (contact) payload.companyContactPerson = contact;

  if (Array.isArray(p.zones)) {
    payload.zones = p.zones.map((z) => buildZoneItem(z as Record<string, unknown>));
  }

  appendExceptions(payload, p);

  return payload;
}

export interface PackageBuilderDeleteOptions {
  _id: string;
  branch: string;
  packageName?: string;
}

/** Body for PUT .../branches/:branchId/package-builders/:id/actions/delete (DeletePackageBuilderDto). */
export function buildPackageBuilderDeletePayload(
  options: PackageBuilderDeleteOptions,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    _id: options._id,
    branch: options.branch,
  };
  if (options.packageName) {
    payload.packageName = options.packageName;
  }
  return payload;
}
